import { useState } from "react";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { ApplicationDetails } from "../../../components/ApplicationDetails";
import { Divider } from "../../../components/Divider";
import { FormText } from "../../../components/Input/FormText";
import { type CommonData, Country } from "../../../types/common";
import {
  formatAccountNumber,
  getAccountNumber,
  validateAccountNumberNorway
} from "../../../utils/account.helper";
import { updatePersonName } from "../../../utils/contact.helper";
import { isEmailValid, isPhoneNumberValid } from "../../../utils/validation";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import type { ComponentConfig } from "../types";

type ErrorKeys =
  | "firstName"
  | "lastName"
  | "phoneNumber"
  | "emailAddress"
  | "firstNameCoapplicant"
  | "lastNameCoapplicant"
  | "phoneNumberCoapplicant"
  | "emailAddressCoapplicant"
  | "autogiroAccountNumber"
  | "disbursementAccountNumber";
type ValidationErrors = Partial<Record<ErrorKeys, string>>;

const ChangeContactInformation = (flow: any) => {
  const {
    updatedFirstName: updatedFirstNameData,
    updatedLastName: updatedLastNameData,
    updatedFirstNameCoApplicant: updatedFirstNameCoApplicantData,
    updatedLastNameCoApplicant: updatedLastNameCoApplicantData,
    updatedEmailAddress: updatedEmailAddressData,
    updatedPhoneNumber: updatedPhoneNumberData,
    updatedEmailAddressCoApplicant: updatedEmailAddressCoApplicantData,
    updatedPhoneNumberCoApplicant: updatedPhoneNumberCoApplicantData,
    updatedAutogiroAccountNumber: updatedAutogiroAccountNumberData,
    updatedDisbursementAccountNumber: updatedDisbursementAccountNumberData
  } = flow?.task?.data ?? {};

  const data = flow?.flow?.data as CommonData;
  const { agentApiApplication, country } = data ?? {};
  const { applicant, coapplicant } = agentApiApplication ?? {};

  // Prefill the names like the case overview does: manually changed values
  // first, then the population register (Bisnode in Sweden, DVV in Finland),
  // then the application itself.
  updatePersonName(data, applicant, true);
  updatePersonName(data, coapplicant, false);

  const { firstName, lastName, emailAddress, mobileNumber } = applicant ?? {};
  const {
    firstName: firstNameCoApp,
    lastName: lastNameCoApp,
    emailAddress: emailAddressCoApp,
    mobileNumber: mobileNumberCoApp
  } = coapplicant ?? {};

  const [updatedFirstName, setUpdatedFirstName] = useState<string>(
    updatedFirstNameData || firstName || ""
  );
  const [updatedLastName, setUpdatedLastName] = useState<string>(
    updatedLastNameData || lastName || ""
  );
  const [updatedFirstNameCoapplicant, setUpdatedFirstNameCoapplicant] =
    useState<string>(updatedFirstNameCoApplicantData || firstNameCoApp || "");
  const [updatedLastNameCoapplicant, setUpdatedLastNameCoapplicant] =
    useState<string>(updatedLastNameCoApplicantData || lastNameCoApp || "");
  const [updatedEmailAddress, setUpdatedEmailAddress] = useState<string>(
    updatedEmailAddressData || ""
  );
  const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState<string>(
    updatedPhoneNumberData || ""
  );
  const [updatedEmailAddressCoapplicant, setUpdatedEmailAddressCoapplicant] =
    useState<string>(updatedEmailAddressCoApplicantData || "");
  const [updatedPhoneNumberCoapplicant, setUpdatedPhoneNumberCoapplicant] =
    useState<string>(updatedPhoneNumberCoApplicantData || "");
  const [updatedAutogiroAccountNumber, setUpdatedAutogiroAccountNumber] =
    useState<string>(updatedAutogiroAccountNumberData || "");
  const [
    updatedDisbursementAccountNumber,
    setUpdatedDisbursementAccountNumber
  ] = useState<string>(updatedDisbursementAccountNumberData || "");

  const [error, setError] = useState<ValidationErrors | null>(null);

  // Empty values are never sent (the backend drops them), so only a filled-in,
  // different value counts as a change. Email, phone and account fields start
  // out empty, which used to enable Complete with nothing to apply.
  const hasChanged = (
    newValue: string | null,
    originalValue: string | undefined
  ) => {
    return !!newValue && newValue !== originalValue;
  };

  const canComplete =
    hasChanged(updatedFirstName, firstName) ||
    hasChanged(updatedLastName, lastName) ||
    hasChanged(updatedFirstNameCoapplicant, firstNameCoApp) ||
    hasChanged(updatedLastNameCoapplicant, lastNameCoApp) ||
    hasChanged(updatedEmailAddress, emailAddress) ||
    hasChanged(updatedEmailAddressCoapplicant, emailAddressCoApp) ||
    hasChanged(updatedPhoneNumber, mobileNumber) ||
    hasChanged(updatedPhoneNumberCoapplicant, mobileNumberCoApp) ||
    hasChanged(
      updatedAutogiroAccountNumber,
      getAccountNumber(flow, "autogiro")
    ) ||
    hasChanged(
      updatedDisbursementAccountNumber,
      getAccountNumber(flow, "disbursement")
    );

  const validate = () => {
    let isValid = true;
    const newErrors: ValidationErrors = {};

    if (updatedFirstName) {
      if (updatedFirstName.length < 1) {
        newErrors.firstName = "Please enter a valid first name for applicant.";
        isValid = false;
      }
    }
    if (updatedLastName) {
      if (updatedLastName.length < 1) {
        newErrors.lastName = "Please enter a valid last name for applicant.";
        isValid = false;
      }
    }
    if (updatedFirstNameCoapplicant) {
      if (updatedFirstNameCoapplicant.length < 1) {
        newErrors.firstNameCoapplicant =
          "Please enter a valid first name for Co-applicant.";
        isValid = false;
      }
    }
    if (updatedLastNameCoapplicant) {
      if (updatedLastNameCoapplicant.length < 1) {
        newErrors.lastNameCoapplicant =
          "Please enter a valid last name for Co-applicant.";
        isValid = false;
      }
    }

    if (updatedEmailAddress) {
      if (!isEmailValid(updatedEmailAddress)) {
        newErrors.emailAddress =
          "Please enter a valid email address for applicant.";
        isValid = false;
      }
    }

    if (updatedEmailAddressCoapplicant) {
      if (!isEmailValid(updatedEmailAddressCoapplicant)) {
        newErrors.emailAddressCoapplicant =
          "Please enter a valid email address for Co-applicant & try again.";
        isValid = false;
      }
    }

    if (updatedPhoneNumber) {
      if (!isPhoneNumberValid(updatedPhoneNumber, country)) {
        newErrors.phoneNumber =
          "Please enter a valid phone number for applicant & try again.";
        isValid = false;
      }
    }

    if (updatedPhoneNumberCoapplicant) {
      if (!isPhoneNumberValid(updatedPhoneNumberCoapplicant, country)) {
        newErrors.phoneNumberCoapplicant =
          "Please enter a valid phone number for Co-applicant & try again.";
        isValid = false;
      }
    }

    // Norway pays out to and debits these accounts as entered, so they must be real
    // Norwegian account numbers.
    if (country === Country.NO) {
      const accountFields = [
        [
          "autogiroAccountNumber",
          updatedAutogiroAccountNumber,
          "Autogiro account number"
        ],
        [
          "disbursementAccountNumber",
          updatedDisbursementAccountNumber,
          "Disbursement account number"
        ]
      ] as const;
      for (const [key, value, label] of accountFields) {
        const accountError = value
          ? validateAccountNumberNorway(value, label)
          : null;
        if (accountError) {
          newErrors[key] = accountError;
          isValid = false;
        }
      }
    }

    setError(newErrors);
    return isValid;
  };

  const onComplete = async () => {
    const isValid = validate();
    if (!isValid) {
      return;
    }

    const options: {
      [key: string]: any;
    } = {
      firstName: updatedFirstName,
      lastName: updatedLastName,
      emailAddress: updatedEmailAddress,
      mobileNumber: updatedPhoneNumber,
      firstNameCoapplicant: updatedFirstNameCoapplicant,
      lastNameCoapplicant: updatedLastNameCoapplicant,
      emailAddressCoapplicant: updatedEmailAddressCoapplicant,
      mobileNumberCoapplicant: updatedPhoneNumberCoapplicant,
      autogiroAccountNumber: updatedAutogiroAccountNumber,
      disbursementAccountNumber: updatedDisbursementAccountNumber
    };

    // Create a new options object without the null values
    for (const key of Object.keys(options)) {
      if (options[key] == null) {
        delete options[key];
      }
    }

    await flow.trigger(options);
  };

  const onSave = async () => {
    flow.save({
      updatedFirstName: updatedFirstName,
      updatedLastName: updatedLastName,
      updatedEmailAddress: updatedEmailAddress,
      updatedPhoneNumber: updatedPhoneNumber,
      updatedFirstNameCoapplicant: updatedFirstNameCoapplicant,
      updatedLastNameCoapplicant: updatedLastNameCoapplicant,
      updatedEmailAddressCoapplicant: updatedEmailAddressCoapplicant,
      updatedPhoneNumberCoapplicant: updatedPhoneNumberCoapplicant,
      updatedAutogiroAccountNumber: updatedAutogiroAccountNumber,
      updatedDisbursementAccountNumber: updatedDisbursementAccountNumber
    });
  };

  const formConfig: ComponentConfig[] = [
    {
      component: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
          }}
        >
          <ApplicantInfo applicant={applicant} />
        </div>
      ),
      order: 0
    },
    {
      component: (
        <FormText
          label="First name"
          onChange={(e) => setUpdatedFirstName(e)}
          name="firstname"
          defaultValue={updatedFirstName || firstName || ""}
          required
          hasError={!!error?.firstName}
          errorMessage={error?.firstName}
        />
      ),
      order: 1
    },
    {
      component: (
        <FormText
          label="Last name"
          onChange={(e) => setUpdatedLastName(e)}
          name="lastname"
          defaultValue={updatedLastName || lastName || ""}
          required
          hasError={!!error?.lastName}
          errorMessage={error?.lastName}
        />
      ),
      order: 2
    },
    {
      component: (
        <FormText
          label="Email address"
          onChange={(e) => setUpdatedEmailAddress(e)}
          name="emailaddress"
          defaultValue={updatedEmailAddress || emailAddress || ""}
          required
          hasError={!!error?.emailAddress}
          errorMessage={error?.emailAddress}
        />
      ),
      order: 3
    },
    {
      component: (
        <FormText
          label="Mobile number"
          onChange={(e) => setUpdatedPhoneNumber(e)}
          name="mobilenumber"
          defaultValue={updatedPhoneNumber || mobileNumber || ""}
          required
          hasError={!!error?.phoneNumber}
          errorMessage={error?.phoneNumber}
        />
      ),
      order: 4
    },
    {
      component: (
        <FormText
          label="Autogiro account number"
          onChange={(e) => setUpdatedAutogiroAccountNumber(e)}
          name="autogiroaccountnumber"
          hasError={!!error?.autogiroAccountNumber}
          errorMessage={error?.autogiroAccountNumber}
          defaultValue={
            updatedAutogiroAccountNumber ||
            formatAccountNumber(
              getAccountNumber(flow, "autogiro"),
              true,
              country
            ) ||
            ""
          }
        />
      ),
      order: 5
    },
    {
      component: (
        <FormText
          label="Disbursement account number"
          onChange={(e) => setUpdatedDisbursementAccountNumber(e)}
          name="disbursementaccountnumber"
          hasError={!!error?.disbursementAccountNumber}
          errorMessage={error?.disbursementAccountNumber}
          defaultValue={
            updatedDisbursementAccountNumber ||
            formatAccountNumber(
              getAccountNumber(flow, "disbursement"),
              false,
              country
            ) ||
            ""
          }
        />
      ),
      order: 6
    }
  ];

  if (coapplicant) {
    formConfig.push(
      {
        component: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "0 28px"
            }}
          >
            <Divider />
          </div>
        ),
        order: 99
      },
      {
        component: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem"
            }}
          >
            <ApplicantInfo
              applicant={{ ...coapplicant, isCoApplicant: true }}
            />
          </div>
        ),
        order: 100
      },
      {
        component: (
          <FormText
            label="First name"
            onChange={(e) => setUpdatedFirstNameCoapplicant(e)}
            name="firstnameCoapplicant"
            defaultValue={updatedFirstNameCoapplicant || firstNameCoApp || ""}
            required
            hasError={!!error?.firstNameCoapplicant}
            errorMessage={error?.firstNameCoapplicant}
          />
        ),
        order: 101
      },
      {
        component: (
          <FormText
            label="Last name"
            onChange={(e) => setUpdatedLastNameCoapplicant(e)}
            name="lastnameCoapplicant"
            defaultValue={updatedLastNameCoapplicant || lastNameCoApp || ""}
            required
            hasError={!!error?.lastNameCoapplicant}
            errorMessage={error?.lastNameCoapplicant}
          />
        ),
        order: 102
      },
      {
        component: (
          <FormText
            label="Email address"
            onChange={(e) => setUpdatedEmailAddressCoapplicant(e)}
            name="emailaddressCoapplicant"
            defaultValue={
              updatedEmailAddressCoapplicant || emailAddressCoApp || ""
            }
            required
            hasError={!!error?.emailAddressCoapplicant}
            errorMessage={error?.emailAddressCoapplicant}
          />
        ),
        order: 103
      },
      {
        component: (
          <FormText
            label="Mobile number"
            onChange={(e) => setUpdatedPhoneNumberCoapplicant(e)}
            name="mobilenumber"
            defaultValue={
              updatedPhoneNumberCoapplicant || mobileNumberCoApp || ""
            }
            required
            hasError={!!error?.phoneNumberCoapplicant}
            errorMessage={error?.phoneNumberCoapplicant}
          />
        ),
        order: 104
      }
    ); // Add form config for coapplicant
  }

  const infoConfig: ComponentConfig[] = [
    {
      component: (
        <ApplicationDetails
          agentApiApplication={agentApiApplication}
          country={country}
        />
      ),
      order: 1
    }
  ];

  return (
    <CommonUserTaskNew
      handleComplete={onComplete}
      handleSave={onSave}
      infoConfig={infoConfig.length > 0 ? infoConfig : undefined}
      formConfig={formConfig}
      overrideMasonryColumns={1}
      disableButtons={!canComplete}
    />
  );
};

export default ChangeContactInformation;
